// TODO: i18n — see Auth.js for pattern, replace hardcoded strings with t('namespace.key')
import { useState, useEffect } from 'react'
import { supabase } from '../supabase'
import { Stars, CAT_ICONS } from './ContractorDirectory'
import { formatPhone } from '../utils/formatPhone'

const C = {
  bg: '#F2EDE4', card: '#FDFAF6', border: '#DDD4C6',
  amber: '#B8721F', amberLight: '#D4902A', brown: '#2C1D14',
  muted: '#9A7B68', success: '#2E6B4A', danger: '#B03020',
  successBg: '#EBF5EF', dangerBg: '#FDECEA',
}
const f = 'Georgia, serif'

const S = {
  overlay: {
    position: 'fixed', inset: 0, background: 'rgba(44,29,20,0.55)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 1100, padding: '20px', fontFamily: f,
  },
  modal: {
    background: C.card, borderRadius: '16px', width: '100%',
    maxWidth: '520px', maxHeight: '92vh', overflowY: 'auto',
    boxShadow: '0 8px 40px rgba(44,29,20,0.25)', border: `1px solid ${C.border}`,
  },
  header: {
    padding: '24px 28px 0', display: 'flex',
    justifyContent: 'space-between', alignItems: 'flex-start',
  },
  closeBtn: { background: 'none', border: 'none', fontSize: '22px', cursor: 'pointer', color: C.muted, padding: 0, lineHeight: 1, flexShrink: 0 },
  body: { padding: '20px 28px 28px' },
  catBadge: {
    display: 'inline-block', fontSize: '11px', padding: '3px 10px', borderRadius: '20px',
    background: '#FBF3E8', color: C.amber, border: `1px solid ${C.amber}33`,
    fontFamily: 'Calibri, sans-serif', marginBottom: '10px',
  },
  verifiedBadge: {
    display: 'inline-block', fontSize: '10px', padding: '2px 8px', borderRadius: '20px',
    background: '#EBF5EF', color: C.success, border: `1px solid ${C.success}33`,
    fontFamily: 'Calibri, sans-serif', marginLeft: '6px',
  },
  name: { fontSize: '22px', fontWeight: '700', color: C.brown, marginBottom: '8px', lineHeight: 1.2 },
  ratingRow: { display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' },
  ratingNum: { fontSize: '18px', fontWeight: '700', color: C.amber },
  ratingCount: { fontSize: '13px', color: C.muted, fontFamily: 'Calibri, sans-serif' },
  divider: { borderTop: `1px solid ${C.border}`, margin: '18px 0' },
  sectionTitle: {
    fontSize: '11px', color: C.muted, letterSpacing: '2px',
    textTransform: 'uppercase', marginBottom: '12px', fontFamily: 'Calibri, sans-serif',
  },
  infoRow: {
    display: 'flex', alignItems: 'flex-start', gap: '10px',
    marginBottom: '10px', fontFamily: 'Calibri, sans-serif',
  },
  infoIcon: { fontSize: '16px', flexShrink: 0, marginTop: '1px' },
  infoLabel: { fontSize: '11px', color: C.muted, textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '2px' },
  infoValue: { fontSize: '14px', color: C.brown, fontWeight: '500' },
  description: {
    fontSize: '14px', color: C.brown, fontFamily: 'Calibri, sans-serif',
    lineHeight: 1.65, marginBottom: '0',
  },
  quoteBtn: {
    width: '100%', background: `linear-gradient(135deg, ${C.amber}, ${C.amberLight})`,
    border: 'none', borderRadius: '10px', padding: '15px',
    color: C.card, fontSize: '14px', fontWeight: '700',
    letterSpacing: '2px', textTransform: 'uppercase',
    cursor: 'pointer', fontFamily: f,
  },
  phoneBtn: {
    width: '100%', background: 'transparent',
    border: `1px solid ${C.amber}`, borderRadius: '10px', padding: '14px',
    color: C.amber, fontSize: '14px', fontWeight: '700',
    letterSpacing: '1px', cursor: 'pointer', fontFamily: f,
    display: 'block', textDecoration: 'none', textAlign: 'center', boxSizing: 'border-box',
    marginTop: '10px',
  },
  starRow: { display: 'flex', gap: '8px', marginBottom: '12px', alignItems: 'center' },
  reviewTextarea: {
    width: '100%', background: C.bg, border: `1px solid ${C.border}`,
    borderRadius: '8px', padding: '10px 14px', fontSize: '14px',
    fontFamily: f, color: C.brown, boxSizing: 'border-box',
    outline: 'none', resize: 'vertical', minHeight: '80px', marginBottom: '12px',
  },
  submitBtn: (disabled) => ({
    background: disabled ? C.bg : `linear-gradient(135deg, ${C.amber}, ${C.amberLight})`,
    border: `1px solid ${disabled ? C.border : 'transparent'}`,
    borderRadius: '8px', padding: '11px 20px', fontSize: '13px',
    color: disabled ? C.muted : C.card, fontWeight: '700',
    cursor: disabled ? 'not-allowed' : 'pointer', fontFamily: f,
  }),
  alert: (type) => ({
    borderRadius: '8px', padding: '10px 14px', marginBottom: '12px',
    fontSize: '13px', fontFamily: 'Calibri, sans-serif',
    background: type === 'error' ? C.dangerBg : C.successBg,
    border: `1px solid ${type === 'error' ? C.danger : C.success}44`,
    color: type === 'error' ? C.danger : C.success,
  }),
}

export default function ContractorDetail({ contractor, session, onClose, onReviewSubmitted }) {
  const [myRating, setMyRating] = useState(0)
  const [hoverRating, setHoverRating] = useState(0)
  const [reviewText, setReviewText] = useState('')
  const [existingReview, setExistingReview] = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const [alert, setAlert] = useState(null)
  const [showQuotePlaceholder, setShowQuotePlaceholder] = useState(false)

  useEffect(() => { fetchMyReview() }, [])

  const fetchMyReview = async () => {
    const { data } = await supabase.from('contractor_reviews')
      .select('*')
      .eq('contractor_id', contractor.id)
      .eq('user_id', session.user.id)
      .maybeSingle()
    if (data) {
      setExistingReview(data)
      setMyRating(data.rating)
      setReviewText(data.review || '')
    }
  }

  const handleSubmitReview = async () => {
    if (!myRating) { setAlert({ type: 'error', msg: 'Please select a star rating.' }); return }
    setSubmitting(true)
    setAlert(null)
    try {
      const { error } = await supabase.from('contractor_reviews').upsert({
        contractor_id: contractor.id,
        user_id: session.user.id,
        rating: myRating,
        review: reviewText.trim() || null,
      }, { onConflict: 'contractor_id,user_id' })
      if (error) throw error
      await supabase.rpc('update_contractor_rating', { p_contractor_id: contractor.id })
      setExistingReview({ rating: myRating, review: reviewText })
      setAlert({ type: 'success', msg: existingReview ? 'Review updated — thank you!' : 'Review submitted — thank you!' })
      onReviewSubmitted?.()
    } catch {
      setAlert({ type: 'error', msg: 'Error submitting review. Please try again.' })
    }
    setSubmitting(false)
  }

  return (
    <div style={S.overlay} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div style={S.modal}>
        <div style={S.header}>
          <button
            onClick={onClose}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.muted, fontSize: '15px', padding: '4px 8px 4px 0', display: 'flex', alignItems: 'center', gap: '4px', flexShrink: 0, fontFamily: f }}
          >
            ← Back
          </button>
          <div style={{ flex: 1, paddingLeft: '4px' }}>
            <span style={S.catBadge}>{CAT_ICONS[contractor.category]} {contractor.category}</span>
            {contractor.verified && <span style={S.verifiedBadge}>✓ Verified</span>}
          </div>
        </div>

        <div style={S.body}>
          <div style={S.name}>{contractor.name}</div>

          <div style={S.ratingRow}>
            <Stars rating={contractor.rating} size={20} />
            <span style={S.ratingNum}>{Number(contractor.rating).toFixed(1)}</span>
            <span style={S.ratingCount}>({contractor.review_count} review{contractor.review_count !== 1 ? 's' : ''})</span>
          </div>

          {/* Description / Bio */}
          {(contractor.bio || contractor.description) && (
            <p style={S.description}>{contractor.bio || contractor.description}</p>
          )}

          {/* Credential badges */}
          {(contractor.insured || contractor.bonded || contractor.license_number || contractor.price_range) && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '12px', marginBottom: '4px' }}>
              {contractor.insured && (
                <span style={{ fontSize: '11px', padding: '3px 10px', borderRadius: '20px', background: '#EBF5EF', color: C.success, border: `1px solid ${C.success}33`, fontFamily: 'Calibri, sans-serif' }}>✓ Insured</span>
              )}
              {contractor.bonded && (
                <span style={{ fontSize: '11px', padding: '3px 10px', borderRadius: '20px', background: '#EBF5EF', color: C.success, border: `1px solid ${C.success}33`, fontFamily: 'Calibri, sans-serif' }}>✓ Bonded</span>
              )}
              {contractor.license_number && (
                <span style={{ fontSize: '11px', padding: '3px 10px', borderRadius: '20px', background: C.bg, color: C.muted, border: `1px solid ${C.border}`, fontFamily: 'Calibri, sans-serif' }}>Lic. {contractor.license_number}</span>
              )}
              {contractor.price_range && (
                <span style={{ fontSize: '11px', padding: '3px 10px', borderRadius: '20px', background: '#FBF3E8', color: C.amber, border: `1px solid ${C.amber}33`, fontFamily: 'Calibri, sans-serif' }}>{contractor.price_range}</span>
              )}
            </div>
          )}

          <div style={S.divider} />

          {/* Contact Info */}
          <div style={S.sectionTitle}>Contact & Service Area</div>

          {contractor.service_area && (
            <div style={S.infoRow}>
              <span style={S.infoIcon}>📍</span>
              <div>
                <div style={S.infoLabel}>Service Area</div>
                <div style={S.infoValue}>{contractor.service_area}</div>
              </div>
            </div>
          )}
          {contractor.phone && (
            <div style={S.infoRow}>
              <span style={S.infoIcon}>📞</span>
              <div>
                <div style={S.infoLabel}>Phone</div>
                <div style={S.infoValue}>
                  <a href={`tel:${contractor.phone}`} style={{ color: C.amber, fontWeight: '700', textDecoration: 'none' }}>
                    {formatPhone(contractor.phone)}
                  </a>
                </div>
              </div>
            </div>
          )}
          {contractor.website && (
            <div style={S.infoRow}>
              <span style={S.infoIcon}>🌐</span>
              <div>
                <div style={S.infoLabel}>Website</div>
                <div style={S.infoValue}>
                  <a href={`https://${contractor.website}`} target="_blank" rel="noopener noreferrer"
                    style={{ color: C.amber, textDecoration: 'none' }}>{contractor.website}</a>
                </div>
              </div>
            </div>
          )}

          <div style={S.divider} />

          {/* Actions */}
          <button style={S.quoteBtn} onClick={() => setShowQuotePlaceholder(true)}>
            Request a Quote
          </button>
          {showQuotePlaceholder && (
            <div style={{ background: '#FBF3E8', border: `1px solid ${C.amber}33`, borderRadius: '8px', padding: '12px 14px', marginTop: '10px', fontSize: '13px', color: C.brown, fontFamily: 'Calibri, sans-serif', lineHeight: 1.6 }}>
              📬 Quote requests are coming in Phase 2. For now, call or visit their website directly.
            </div>
          )}
          {contractor.phone && (
            <a href={`tel:${contractor.phone}`} style={S.phoneBtn}>📞 Call {formatPhone(contractor.phone)}</a>
          )}

          <div style={S.divider} />

          {/* Leave a Rating */}
          <div style={S.sectionTitle}>
            {existingReview ? 'Your Review' : 'Leave a Rating'}
          </div>

          {alert && <div style={S.alert(alert.type)}>{alert.msg}</div>}

          <div style={{ marginBottom: '6px', fontSize: '12px', color: C.muted, fontFamily: 'Calibri, sans-serif' }}>
            {existingReview ? 'Tap stars to update your rating' : 'Tap to rate this contractor'}
          </div>
          <div style={S.starRow}>
            <Stars
              rating={myRating}
              size={28}
              interactive
              hovered={hoverRating}
              onHover={setHoverRating}
              onClick={setMyRating}
            />
            {myRating > 0 && (
              <span style={{ fontSize: '13px', color: C.muted, fontFamily: 'Calibri, sans-serif' }}>
                {['', 'Poor', 'Fair', 'Good', 'Great', 'Excellent'][myRating]}
              </span>
            )}
          </div>

          <textarea
            style={S.reviewTextarea}
            placeholder="Optional: share details about your experience..."
            value={reviewText}
            onChange={e => { setReviewText(e.target.value); setAlert(null) }}
          />

          <button
            style={S.submitBtn(!myRating || submitting)}
            onClick={handleSubmitReview}
            disabled={!myRating || submitting}
          >
            {submitting ? 'Submitting...' : existingReview ? 'Update Review' : 'Submit Review'}
          </button>
        </div>
      </div>
    </div>
  )
}
